import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Knowledge.css';

interface UnansweredQuestion {
  id: number;
  question: string;
  context: string;
  ai_response: string;
  confidence_note: string;
  customer_name: string | null;
  created_at: string;
  status: string;
}

const categories = [
  { value: 'feed', label: 'Feed & Nutrition' },
  { value: 'shipping', label: 'Shipping & Delivery' },
  { value: 'ordering', label: 'Ordering & Pricing' },
  { value: 'animal_care', label: 'Animal Care' },
  { value: 'farmhand', label: 'Farmhand App Help' },
  { value: 'policies', label: 'Policies & Procedures' },
  { value: 'products', label: 'Product Information' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'general', label: 'General' },
];

const PendingQuestions: React.FC = () => {
  const [questions, setQuestions] = useState<UnansweredQuestion[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<UnansweredQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('general');
  const [keywords, setKeywords] = useState('');
  const [answeredBy, setAnsweredBy] = useState('both');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const res = await api.get('/knowledge/questions/pending/');
      setQuestions(res.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = async () => {
    if (!selectedQuestion || !answer.trim()) return;

    setSaving(true);
    try {
      await api.post(`/knowledge/questions/${selectedQuestion.id}/create_article/`, {
        answer,
        category,
        keywords,
        answered_by: answeredBy,
      });

      // Refresh list and clear form
      fetchQuestions();
      setSelectedQuestion(null);
      setAnswer('');
      setCategory('general');
      setKeywords('');
      setAnsweredBy('both');
      alert('Article created! Hank & Laura will now use this answer.');
    } catch (error) {
      console.error('Error creating article:', error);
      alert('Error creating article');
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async (questionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Mark this question as not needing an answer?')) return;

    try {
      await api.post(`/knowledge/questions/${questionId}/mark_ignored/`);
      fetchQuestions();
      if (selectedQuestion?.id === questionId) {
        setSelectedQuestion(null);
      }
    } catch (error) {
      console.error('Error ignoring question:', error);
    }
  };

  if (loading) {
    return <div className="knowledge-container"><div className="knowledge-loading">Loading...</div></div>;
  }

  return (
    <div className="knowledge-container">
      <div className="knowledge-header">
        <div className="header-left">
          <Link to="/knowledge" className="back-link">&larr; Knowledge Base</Link>
          <h1>Pending Questions</h1>
          <p className="knowledge-subtitle">Questions Hank & Laura couldn't fully answer</p>
        </div>
        <span className="pending-badge">{questions.length} pending</span>
      </div>

      {questions.length === 0 ? (
        <div className="knowledge-empty">
          <div className="empty-icon">&#127881;</div>
          <h2>All caught up!</h2>
          <p>No pending questions to review</p>
          <Link to="/knowledge" className="btn-action btn-blue">Back to Dashboard</Link>
        </div>
      ) : (
        <div className="pending-layout">
          {/* Questions List */}
          <div className="questions-list-panel">
            <div className="panel-header">
              <h2>Questions</h2>
            </div>
            <div className="questions-scroll">
              {questions.map((q) => (
                <div
                  key={q.id}
                  className={`question-card ${selectedQuestion?.id === q.id ? 'selected' : ''}`}
                  onClick={() => setSelectedQuestion(q)}
                >
                  <p className="question-card-text">{q.question}</p>
                  <p className="question-card-meta">
                    {q.customer_name || 'Unknown'} &bull; {new Date(q.created_at).toLocaleDateString()}
                  </p>
                  {q.ai_response && (
                    <details className="ai-response-details">
                      <summary>AI's response</summary>
                      <p className="ai-response-text">{q.ai_response}</p>
                    </details>
                  )}
                  <button
                    onClick={(e) => handleIgnore(q.id, e)}
                    className="btn-ignore"
                  >
                    Ignore
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Answer Form */}
          <div className="answer-form-panel">
            <div className="panel-header">
              <h2>Create Answer</h2>
            </div>
            <div className="panel-content">
              {selectedQuestion ? (
                <>
                  <div className="selected-question-box">
                    <label>Question:</label>
                    <p>{selectedQuestion.question}</p>
                    {selectedQuestion.confidence_note && (
                      <p className="confidence-note">
                        <em>Why flagged: {selectedQuestion.confidence_note}</em>
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="answer">Answer (how should Hank or Laura respond?)</label>
                    <textarea
                      id="answer"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Type the answer customers should receive..."
                      rows={5}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="category">Category</label>
                      <select
                        id="category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        {categories.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="answeredBy">Who answers?</label>
                      <select
                        id="answeredBy"
                        value={answeredBy}
                        onChange={(e) => setAnsweredBy(e.target.value)}
                      >
                        <option value="both">Either Hank or Laura</option>
                        <option value="hank">Hank (farming/feed)</option>
                        <option value="laura">Laura (data/logistics)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="keywords">Keywords (comma-separated, helps AI find this answer)</label>
                    <input
                      type="text"
                      id="keywords"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      placeholder="e.g., layer feed, winter, protein"
                    />
                  </div>

                  <button
                    onClick={handleCreateArticle}
                    disabled={saving || !answer.trim()}
                    className="btn-create-article"
                  >
                    {saving ? 'Saving...' : 'Create Knowledge Article'}
                  </button>
                </>
              ) : (
                <div className="no-selection">
                  <p>&larr; Select a question to answer</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingQuestions;
